import { computed, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { environment } from '@environments/environment';
import { NavChild, NavItem } from '@models/nav-item';
import { AuthService } from '@services/auth.service';

@Injectable( { providedIn: 'root' } )

export class NavService {

    readonly #router = inject( Router );
    readonly #auth = inject( AuthService );
    readonly authService = this.#auth; // exposed for template (org display name)

    // ── Feature flags ─────────────────────────────────────────────────────────
    readonly #showUnLocodes = environment.optionalModules.showUnLocodes;
    readonly #showService = environment.optionalModules.showServiceModules; // ← NEU

    // ── Nav items — reactive via computed ─────────────────────────────────────
    readonly navItems = computed<NavItem[]>( () => {
        const items: NavItem[] = [];
        // Community
        items.push( {
            id: 'community',
            label: $localize`:@@sidenav-item-community:Community`,
            icon: 'hub',
            route: this.#auth.isCommunityMember()
                ? '/scope-community/community-members'
                : '/scope-community',
        } );

        // Tools — nur anzeigen wenn mindestens ein Child aktiv ist
        const toolsChildren: NavChild[] = [];

        toolsChildren.push( { id: 'tools-scope-runtime', label: 'Scope Runtime', route: '/tools/scope-runtime' } );

        toolsChildren.push( {
            id: 'tools-exchange-rate',
            label: $localize`:@@menu-item-exchange-rate-calculator:Exchange rate calculator`,
            route: '/tools/exchange-rate-calculator',
        } );

        if ( this.#showUnLocodes ) {
            toolsChildren.push( { id: 'tools-unlocodes', label: 'UN/LOCODEs', route: '/tools/unlocodes' } );
        }

        items.push( {
            id: 'tools',
            label: $localize`:@@menu-item-tools-menu:Tools`,
            icon: 'cases',
            children: toolsChildren,
        } );

        // Only for RSI Super Admin
        if ( this.#auth.isSuperAdmin() ) {
            const serviceChildren: NavChild[] = [];

            serviceChildren.push( {
                id: 'service-tenants',
                label: $localize`:@@menu-item-tenants:Scope tenants`,
                route: '/tenants',
            } );

            if ( this.#showService ) {
                serviceChildren.push( {
                    id: 'service-link',
                    label: $localize`:@@menu-item-service-modules:Service modules`,
                    route: '/service',
                } );
            }

            items.push( {
                id: 'service',
                label: $localize`:@@menu-item-service-menu:Service`,
                icon: 'rocket_launch',
                children: serviceChildren,
            } );
        }

        return items;
    } );

    // ── Admin item — bleibt unten in der Rail ─────────────────────────────────
    readonly adminItem = computed<NavItem | null>( () => {
        if ( !this.#auth.isLoggedIn() ) return null;

        const children: NavChild[] = [];

        // Manage Organization
        if ( this.#auth.isAdmin() || this.#auth.isSuperAdmin() ) {
            children.push( {
                id: 'admin-org',
                label: $localize`:@@toolbar-menu-manage-organization:Manage Organization`,
                route: '/administration/organization',
            } );
        }

        if ( !children.length ) return null;

        return {
            id: 'admin',
            label: $localize`:@@sidenav-item-admin:Admin`,
            icon: 'settings',
            children,
        };
    } );

    // ── Navigation ────────────────────────────────────────────────────────────
    navigateTo( route: string ): void {
        this.#router.navigateByUrl( route );
    }

    // ── Active state check ────────────────────────────────────────────────────
    isItemActive( item: NavItem ): boolean {
        if ( !item.children?.length ) return false;
        const currentUrl = this.#router.url.split( '?' )[ 0 ].split( ';' )[ 0 ];
        return item.children.some( child => currentUrl.startsWith( child.route ) );
    }
}
